import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { Plus, Trash2 } from 'lucide-react';
import api from '@/services/apiClient';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import EmptyState from '@/components/ui/EmptyState';
import Spinner from '@/components/ui/Spinner';
import { fmtDate, fmtNum } from '@/lib/enums';

export const TEST_TYPES = [
  'flexao',
  'abdominal',
  'prancha',
  'corrida_12min',
  'forca_supino',
  'forca_agachamento',
  'forca_terra',
];
const STRENGTH = ['forca_supino', 'forca_agachamento', 'forca_terra'];

/** Testes físicos do aluno: gráfico por tipo + registro inline. */
export default function FitnessTestsSection({ userId }) {
  const { t } = useTranslation();
  const [type, setType] = useState('forca_supino');
  const [tests, setTests] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), value: '', reps: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = () =>
    api
      .get('/evolution/fitness-tests', { userId, type })
      .then((res) => setTests((res.data || res).tests || []))
      .catch(() => setTests([]));

  useEffect(() => {
    setTests(null);
    if (userId) load();
  }, [userId, type]);

  const isStrength = STRENGTH.includes(type);
  const chart = (tests || []).map((x) => ({
    d: (x.date || '').slice(5),
    v: Number(x.value),
    rm: x.est1rm != null ? Number(x.est1rm) : null,
  }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.post('/evolution/fitness-tests', {
        userId,
        type,
        date: form.date,
        value: Number(form.value),
        reps: isStrength ? Number(form.reps) || null : null,
      });
      setForm((f) => ({ ...f, value: '', reps: '' }));
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/evolution/fitness-tests/${id}`).catch(() => {});
    await load();
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <Select
          label={t('students.tests.type')}
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={TEST_TYPES.map((tt) => ({ value: tt, label: t(`students.tests.types.${tt}`) }))}
          style={{ minWidth: 200 }}
        />
        <Input label={t('students.tests.date')} type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
        <Input label={t(`students.tests.valueLabel.${isStrength ? 'kg' : type === 'prancha' ? 'seconds' : type === 'corrida_12min' ? 'meters' : 'reps'}`)} type="number" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} style={{ width: 130 }} />
        {isStrength && (
          <Input label={t('students.tests.reps')} type="number" value={form.reps} onChange={(e) => setForm((f) => ({ ...f, reps: e.target.value }))} style={{ width: 100 }} />
        )}
        <Button loading={saving} disabled={!form.value} onClick={save}><Plus size={14} /> {t('students.tests.add')}</Button>
      </div>
      {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}

      {tests === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}><Spinner /></div>
      ) : tests.length === 0 ? (
        <EmptyState title={t('students.tests.emptyTitle')} description={t('students.tests.emptyDesc')} />
      ) : (
        <>
          {chart.length >= 2 && (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="d" stroke="var(--low)" fontSize={11} />
                <YAxis stroke="var(--low)" fontSize={11} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: 'var(--surf2)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--hi)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="v" stroke="var(--volt)" strokeWidth={2} dot={false} name={t('students.tests.result')} />
                {isStrength && <Line type="monotone" dataKey="rm" stroke="var(--frost)" strokeWidth={2} dot={false} name={t('students.tests.est1rm')} />}
              </LineChart>
            </ResponsiveContainer>
          )}
          <div style={{ display: 'grid', gap: 6 }}>
            {[...tests].reverse().map((x, i, arr) => {
              const prev = arr[i + 1];
              const delta = prev ? Math.round((Number(x.value) - Number(prev.value)) * 10) / 10 : null;
              return (
                <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surf)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 13 }}>
                  <span style={{ color: 'var(--mid)', width: 90 }}>{fmtDate(x.date)}</span>
                  <strong style={{ color: 'var(--hi)' }}>
                    {fmtNum(x.value)} {isStrength ? 'kg' : type === 'prancha' ? 's' : type === 'corrida_12min' ? 'm' : 'reps'}
                    {isStrength && x.reps ? ` × ${x.reps}` : ''}
                  </strong>
                  {isStrength && x.est1rm != null && (
                    <span style={{ color: 'var(--frost)' }}>1RM ≈ {fmtNum(x.est1rm)}kg</span>
                  )}
                  {delta != null && (
                    <span style={{ color: delta >= 0 ? 'var(--volt)' : 'var(--warning)' }}>{delta > 0 ? '+' : ''}{delta}</span>
                  )}
                  <span style={{ flex: 1 }} />
                  <button type="button" onClick={() => remove(x.id)} style={{ background: 'none', border: 'none', color: 'var(--low)', padding: 4 }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
